import exchanges from '@/constants/exchanges';
import ReportNextSteps from '@components/ReportNextSteps';
import ReportSubSection from '@components/ReportSubSection';
import {
  COMPANY_VALUE_ANCHOR,
  COMPANY_FUTURE_ANCHOR,
  COMPANY_HEALTH_ANCHOR,
  COMPANY_DIVIDEND_ANCHOR,
  COMPANY_MANAGEMENT_ANCHOR,
} from '@/constants/anchors';
import { kebabCase } from 'lodash';
import useInvestingIdea from '@/pages/CompanyReport/hooks/useInvestingIdea';
import loadable from '@loadable/component';
import { GridViewOptions } from '@/pages/CompanyReport/constants/constants';
import { useTracking } from 'react-tracking';
import { useVisibleSectionsIsPristine } from '@/pages/CompanyReport/components/VisibleSectionsContextProvider';

const NextSteps = loadable(
  () => import('@/pages/CompanyReport/components/Overview/components/NextSteps')
);

export interface PastNextStepsProps {
  countryISO: string;
  scoreFuture: number;
  scoreValue: number;
  scoreHealth: number;
  companyName: string;
  tickerSymbol: string;
  scoreDividend: number;
  exchangeSymbol: string;
  CEOCompensation?: number;
  isDataMeaningless: boolean;
  industryPrimaryName: string;
}
const PastNextSteps = ({
  scoreFuture,
  scoreValue,
  countryISO,
  scoreHealth,
  companyName,
  tickerSymbol,
  scoreDividend,
  exchangeSymbol,
  CEOCompensation,
  isDataMeaningless,
  industryPrimaryName,
}: PastNextStepsProps) => {
  const isPristine = useVisibleSectionsIsPristine();
  const payload = useInvestingIdea(
    'strong-performing-companies',
    GridViewOptions.GRID_VIEW_STRONG_PAST_PERFORMING,
    countryISO.toLowerCase(),
    kebabCase(industryPrimaryName)
  );
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'PastNextSteps',
  });

  return (
    <Track>
      <ReportSubSection
        titleElement={isPristine ? 'h2' : 'h3'}
        title="Discover strong past performing companies"
        body={
          <ReportNextSteps>
            {isDataMeaningless && (
              <>
                {scoreValue > 2 ? (
                  <li>
                    <span>{`Examine whether ${companyName} is trading at `}</span>
                    <a
                      href={`#${COMPANY_VALUE_ANCHOR}`}
                      onClick={() =>
                        trackEvent({
                          action: 'click',
                          modifier: 'other-section',
                          destinationSection: COMPANY_VALUE_ANCHOR,
                        })
                      }
                    >
                      an attractive price
                    </a>
                    <span>
                      {` based on how much it is expected to earn in the future, and relative to its industry peers and the wider market.`}
                    </span>
                  </li>
                ) : scoreFuture > 2 ? (
                  <li>
                    <span>Check out </span>
                    <a
                      href={`#${COMPANY_FUTURE_ANCHOR}`}
                      onClick={() =>
                        trackEvent({
                          action: 'click',
                          modifier: 'other-section',
                          destinationSection: COMPANY_FUTURE_ANCHOR,
                        })
                      }
                    >
                      {`${companyName}'s forecasted future earnings and revenues`}
                    </a>
                    <span>{` from industry analysts to understand how the company is expected to perform in the future against its competitors and the wider market.`}</span>
                  </li>
                ) : scoreHealth > 3 ? (
                  <li>
                    <span>Examine </span>
                    <a
                      href={`#${COMPANY_HEALTH_ANCHOR}`}
                      onClick={() =>
                        trackEvent({
                          action: 'click',
                          modifier: 'other-section',
                          destinationSection: COMPANY_HEALTH_ANCHOR,
                        })
                      }
                    >
                      {`${companyName}'s financial health`}
                    </a>
                    <span>
                      {` to determine how well-positioned it is against times of financial stress by looking at its level of debt over time and how much cash it has left.`}
                    </span>
                  </li>
                ) : scoreDividend > 3 ? (
                  <li>
                    <span>{`${companyName} is a dividend payer. Have a look at `}</span>
                    <a
                      href={`#${COMPANY_DIVIDEND_ANCHOR}`}
                      onClick={() =>
                        trackEvent({
                          action: 'click',
                          modifier: 'other-section',
                          destinationSection: COMPANY_DIVIDEND_ANCHOR,
                        })
                      }
                    >
                      its current yield, dividend levels over time
                    </a>
                    <span>{` and whether analysts believe the payout ratio will be sustainable moving forward.`}</span>
                  </li>
                ) : CEOCompensation !== null ? (
                  <li>
                    <span>{`When a company lacks a financial track record, its management’s track record becomes important. Corporate governance also plays an important role in a young business as board members are seen as highly-experienced leaders to help steer the company into the future. Take a look at `}</span>
                    <a
                      href={`#${COMPANY_MANAGEMENT_ANCHOR}`}
                      onClick={() =>
                        trackEvent({
                          action: 'click',
                          modifier: 'other-section',
                          destinationSection: COMPANY_MANAGEMENT_ANCHOR,
                        })
                      }
                    >
                      {`${companyName}'s management and board experience and expertise`}
                    </a>
                    <span>{` to assess their ability to deliver on their strategic promises.`}</span>
                  </li>
                ) : null}
                {exchangeSymbol === 'OTCPK' && (
                  <li>
                    <span>{`While we do not consider unaudited financials to be a reliable enough to include in our analysis, you can access them on the `}</span>
                    <a
                      target="_blank"
                      rel="noreferrer noopener nofollow"
                      href={`${
                        exchanges[exchangeSymbol]
                      }/stock/${tickerSymbol.replace('.', '')}/financials`}
                      onClick={() =>
                        trackEvent({
                          action: 'click',
                          modifier: 'external_link',
                          url: exchanges[exchangeSymbol],
                        })
                      }
                    >
                      OTC Markets Website
                    </a>
                    <span>{`. If you are looking for more of a qualitative research into the company, you can access ${companyName}'s filings and announcements `}</span>
                    <a
                      target="_blank"
                      rel="noreferrer noopener nofollow"
                      href={`${
                        exchanges[exchangeSymbol]
                      }/stock/${tickerSymbol.replace('.', '')}/disclosure`}
                      onClick={() =>
                        trackEvent({
                          action: 'click',
                          modifier: 'external_link',
                          url: exchanges[exchangeSymbol],
                        })
                      }
                    >
                      here.
                    </a>
                  </li>
                )}
              </>
            )}
            <li>
              {typeof payload !== 'undefined' && (
                <NextSteps
                  link={payload.linkToInvestingIdea}
                  industry={industryPrimaryName}
                  section="past"
                />
              )}
            </li>
          </ReportNextSteps>
        }
      />
    </Track>
  );
};

export default PastNextSteps;
